 
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { AuthService } from '../services/auth/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { HeaderComponent } from '../login/header/header.component';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { ConfigurationClientService } from '../services/client/configuration-client.service';

// Custom validator for password matching
function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');

  if (password && confirmPassword && password.value !== confirmPassword.value) {
    return { passwordMismatch: true };
  }
  return null;
}

@Component({
  selector: 'app-signup',
  imports: [CommonModule, ReactiveFormsModule, HeaderComponent],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('600ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
})
export class SignupComponent implements OnInit {
  signupForm!: FormGroup;
  isLoading = false;
  showPassword = false;
  showConfirmPassword = false;
  errorMessage: string | null = null;
  returnUrl: string | null;
  isAuthenticated$!: Observable<boolean>;
  defaultLandingRoute = '/chat';

  // Feature flags
  showSocialLogin = true;
  showGoogleLogin = true;
  showAppleLogin = false;
  showFacebookLogin = false;
  showNameCapture = false;
  showTermsAcceptance = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private config: ConfigurationClientService
  ) {
    this.isAuthenticated$ = this.authService.isAuthenticatedWait();
    this.returnUrl = config.landingRoute;
  }

  ngOnInit(): void {
    // Initialize form with validators
    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', this.showNameCapture ? Validators.required : Validators.nullValidator],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
      termsAccepted: [false, this.showTermsAcceptance ? Validators.requiredTrue : Validators.nullValidator]
    }, { validators: passwordMatchValidator });

    // Get the return URL from the route parameters or default to landing route
    this.returnUrl =
      this.route.snapshot.queryParams['returnUrl'] || this.returnUrl;

    // Check if user is already logged in
    this.isAuthenticated$.subscribe((isAuth) => {
      if (isAuth) {
        this.router.navigate([this.returnUrl]); // Redirect to the return URL if authenticated
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (this.signupForm.valid) {
      this.isLoading = true;
      try {
        const { email, password, name } = this.signupForm.value;
        const credentials = { email, password, name };
        await this.authService.signUp(credentials);
        this.showMessage('Account created successfully!', 'bottom');

        // Store the email in local storage
        localStorage.setItem('sb_user_email', email);
        
        // Navigate to the thank you page
        this.router.navigate(['/thank-you']);
      } catch (error: Error | any) {
        console.error('Signup error:', error?.message);
        this.showMessage('Failed to create account: ' + error?.message, 'top');
        this.handleAuthError(error);
      } finally {
        this.isLoading = false;
      }
    } else {
      // Mark all fields as touched to trigger validation visuals
      Object.keys(this.signupForm.controls).forEach(key => {
        const control = this.signupForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  async signUpWithProvider(
    provider: 'google' | 'apple' | 'facebook'
  ): Promise<void> {
    this.isLoading = true;
    try {
      await this.authService.signInWithProvider(provider);
      this.router.navigate([this.returnUrl]);
    } catch (error: Error | any) {
      console.error(`${provider} sign-up error:`, error);
      this.handleAuthError(error);
    } finally {
      this.isLoading = false;
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  handleAuthError(error: Error | any): void {
    console.error('Authentication error:', error);
    this.errorMessage = error?.message;
  }

  showMessage(
    message: string,
    verticalPosition: 'top' | 'bottom' = 'top'
  ) {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: verticalPosition,
    });
  }
}